import {
  type Column,
  createConnectionRenderer,
  DateRenderer,
  type ExplorerDataProvider,
  filterToPostGraphileFilter,
  FilterTypes,
  IconName,
  type ItemSelection,
  Modal,
  type PageHeaderActionProps,
  SelectionExplorer,
  sortToPostGraphileOrderBy,
} from '@axinom/mosaic-ui';
import React from 'react';
import { config } from '../../../../../config';
import {
  AvailableUserRolesDocument,
  type AvailableUserRolesQuery,
  type UserRoleFilter,
  UserRolesOrderBy,
  type UserRoleTagAssignmentsConnection,
} from '../../../../../generated/id-service-access-management';
import { environmentApolloClient } from '../../../../environmentApolloClient';
import type { UserRoleAssignment } from './models';

type UserRoleData = NonNullable<
  AvailableUserRolesQuery['filtered']
>['nodes'][number];

export const RoleSelectionExplorer: React.FC<{
  environmentId: string;
  isModalShown: boolean;
  initialRoles: UserRoleAssignment[];
  currentRoles: UserRoleAssignment[];
  title: string;
  onNewRoleAssignments: (role: UserRoleAssignment) => void;
  onClose: () => void;
}> = ({
  environmentId,
  isModalShown = false,
  initialRoles,
  currentRoles,
  title,
  onNewRoleAssignments,
  onClose,
}) => {
  const actions: PageHeaderActionProps[] = [
    {
      label: 'Cancel',
      icon: IconName.X,
      onClick: onClose,
    },
  ];

  const columns: Column<UserRoleData>[] = [
    {
      propertyName: 'name',
      label: 'Name',
      size: '150px',
    },
    {
      propertyName: 'userRoleTagAssignments',
      label: 'Tags',
      size: '150px',
      render: createConnectionRenderer<UserRoleTagAssignmentsConnection>(
        (node) => {
          return node.tag;
        },
      ),
    },
    {
      propertyName: 'description',
      label: 'Description',
    },
    {
      propertyName: 'createdAt',
      label: 'Created At',
      size: '175px',
      render: DateRenderer,
    },
    {
      propertyName: 'updatedAt',
      label: 'Last Modified At',
      size: '175px',
      render: DateRenderer,
    },
  ];

  const onItemSelectedHandler = (
    selection: ItemSelection<UserRoleData>,
  ): void => {
    const items = selection.mode === 'SINGLE_ITEMS' ? selection.items : [];
    const [item] = items ?? [];

    // attempt to find an item that was already assigned on the server but removed from DDL
    const removedItem: UserRoleAssignment | undefined = initialRoles.find(
      (role) => role.id === item.id,
    );
    if (removedItem !== undefined) {
      onNewRoleAssignments(removedItem);
    } else {
      onNewRoleAssignments({
        id: item.id,
        name: item.name,
        description: item.description,
      });
    }

    onClose();
  };

  // Data provider
  const dataProvider: ExplorerDataProvider<UserRoleData> = {
    loadData: async ({ pagingInformation, sorting, filters }) => {
      const envApolloClient = environmentApolloClient(
        environmentId,
        new URL(`/graphql`, config.accessManagementBaseUrl).toString(),
      );

      const result = await envApolloClient.query<AvailableUserRolesQuery>({
        query: AvailableUserRolesDocument,
        variables: {
          filter: filterToPostGraphileFilter<UserRoleFilter>(filters, {
            name: 'includesInsensitive',
            description: 'includesInsensitive',
            userRoleTagAssignments: [
              'some',
              'tag',
              'name',
              'includesInsensitive',
            ],
            createdAt: (value) => {
              const [startDate, endDate] = String(value).split('|');
              return {
                greaterThan: startDate,
                lessThan: endDate,
              };
            },
            updatedAt: (value) => {
              const [startDate, endDate] = String(value).split('|');
              return {
                greaterThan: startDate,
                lessThan: endDate,
              };
            },
          }),
          orderBy: sortToPostGraphileOrderBy(sorting, UserRolesOrderBy),
          after: pagingInformation,
        },
        fetchPolicy: 'network-only',
      });

      // TODO: Current Roles filtering should done in the back end.
      const currentIds: string[] = currentRoles.map((role) => role.id);
      let roleData: UserRoleData[] = [];

      let totalFiltered = 0; // total amount of roles removed by the FE

      if (result.data.filtered?.nodes !== undefined) {
        roleData = result.data.filtered?.nodes.filter(
          (node) => !currentIds.includes(node.id),
        );

        totalFiltered =
          (result.data.nonFiltered?.totalCount as number) - currentIds.length;
      }

      return {
        data: roleData,
        totalCount: totalFiltered,
        filteredCount: roleData.length,
        hasMoreData: result.data.filtered?.pageInfo.hasNextPage ?? false,
        pagingInformation: result.data.filtered?.pageInfo.endCursor,
      };
    },
  };

  return (
    <>
      <Modal isShown={isModalShown} onBackDropClicked={onClose}>
        <SelectionExplorer<UserRoleData>
          title={title}
          columns={columns}
          dataProvider={dataProvider}
          stationKey={'RoleSelection'}
          actions={actions}
          onSelection={onItemSelectedHandler}
          allowBulkSelect={false}
          enableSelectAll={false}
          defaultSortOrder={{ column: 'updatedAt', direction: 'desc' }}
          filterOptions={[
            {
              label: 'Name',
              property: 'name',
              type: FilterTypes.FreeText,
            },
            {
              label: 'Description',
              property: 'description',
              type: FilterTypes.FreeText,
            },
            {
              label: 'Tags',
              property: 'userRoleTagAssignments',
              type: FilterTypes.FreeText,
            },
            {
              label: 'Creation Period',
              property: 'createdAt',
              type: FilterTypes.FreeText,
            },
            {
              label: 'Modification Period',
              property: 'updatedAt',
              type: FilterTypes.FreeText,
            },
          ]}
        />
      </Modal>
    </>
  );
};
